import { useState, useEffect, useMemo } from 'react';
import { useParams } from '@hooks/useParams';
import { EventsList } from '../components/EventsList';
import { NotificationTypes, notificationTypesReadable } from '@dynamika-shared/types/subscription';
import { notificationTypesDescriptions } from '../constants';
import { useClinicalTrialSubscriptionSlice } from '@restore/clinicalTrialSubscription/clinicalTrialSubscription.slice';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { AddEditSubscriptionModal } from '../components/AddEditSubscriptionModal';
import { DeleteSubscriptionModal } from '../components/DeleteSubscriptionModal';
import { SubscriptionDetailView } from '../components/SubscriptionDetailView';
import { useSubscriptionFilters } from '../hooks/useSubscriptionFilters';
import { useSubscriptionActions } from '../hooks/useSubscriptionActions';

export const ClinicalTrialNotificationsConfiguration = () => {
  const { id: clinicalTrialId } = useParams();
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue, debouncedSearchValue] = useDebouncedValue('');
  const [selectedKey, setSelectedKey] = useState<string | null>(
    NotificationTypes.UPLOAD_COMPLETED
  );

  const {
    counts,
    fetchNotificationCounts,
    subscriptionsLoading,
    notificationsCountLoading,
    notificationsCountError,
    subscriptions,
    fetchSubscriptions,
    allSubscriptionsForType,
    fetchAllSubscriptionsForType,
  } = useClinicalTrialSubscriptionSlice((state, actions) => ({
    counts: state.notificationsCount ?? {},
    subscriptionsLoading: state.subscriptionsLoading,
    notificationsCountLoading: state.countLoading,
    notificationsCountError: state.countError,
    fetchNotificationCounts: actions.fetchNotificationCounts,
    subscriptions: state.subscriptions,
    fetchSubscriptions: actions.fetchSubscriptions,
    allSubscriptionsForType: state.allSubscriptionsForType,
    fetchAllSubscriptionsForType: actions.fetchAllSubscriptionsForType,
  }));

  const {
    subFilters,
    filters,
    allFilters,
    toggleActiveFilter,
    toggleSelectedFilter,
    selectedFilters,
    filterOnClick,
  } = useSubscriptionFilters(subscriptions?.content || []);

  const {
    sortBy,
    isAddEditModalOpen,
    isDeleteModalOpen,
    selectedSubscription,
    shouldRefetch,
    setShouldRefetch,
    handleToggleSubscription,
    handleSubscribeAll,
    handleUnsubscribeAll,
    onSort,
    handleAddSubscription,
    handleAction,
    handleSubmitSubscription,
    handleConfirmDelete,
    handleCloseModals,
    handlePaginationChange,
  } = useSubscriptionActions({
    clinicalTrialId,
    selectedKey,
  });

  useEffect(() => {
    if (clinicalTrialId) {
      fetchNotificationCounts({ clinicalTrialId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId]);

  useEffect(() => {
    if (clinicalTrialId && selectedKey) {
      fetchSubscriptions({
        clinicalTrialId,
        notificationType: selectedKey as NotificationTypes,
        search: debouncedSearchValue,
        scope: selectedFilters.scope,
        type: selectedFilters.type,
        sort: sortBy,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicalTrialId, selectedKey, selectedFilters.scope, selectedFilters.type, debouncedSearchValue, sortBy]);

  useEffect(() => {
    if (shouldRefetch && !subscriptionsLoading && clinicalTrialId && selectedKey) {
      setShouldRefetch(false);
      fetchSubscriptions({
        clinicalTrialId,
        notificationType: selectedKey as NotificationTypes,
        search: debouncedSearchValue,
        scope: selectedFilters.scope,
        type: selectedFilters.type,
        sort: sortBy,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefetch, subscriptionsLoading]);

  useEffect(() => {
    if (isAddEditModalOpen && clinicalTrialId && selectedKey) {
      fetchAllSubscriptionsForType({
        clinicalTrialId,
        notificationType: selectedKey as NotificationTypes,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddEditModalOpen, clinicalTrialId, selectedKey]);

  const allEvents = useMemo(
    () =>
      Object.values(NotificationTypes).map(type => ({
        key: type,
        title: notificationTypesReadable[type],
        description: notificationTypesDescriptions[type],
        count: counts[type] ?? 0,
        selected: type === selectedKey,
      })),
    [counts, selectedKey]
  );

  return (
    <>
      <div className='flex flex-row gap-4 mt-4 h-[calc(100vh-200px)]'>
        <div>
          <EventsList
            items={allEvents}
            searchValue={search}
            onSearchChange={setSearch}
            onSelect={item => setSelectedKey(item.key)}
            loading={notificationsCountLoading}
            error={notificationsCountError}
          />
        </div>
        {selectedKey && (
          <SubscriptionDetailView
            selectedNotificationType={selectedKey as NotificationTypes}
            subscriptions={subscriptions}
            loading={subscriptionsLoading}
            searchValue={searchValue}
            sortBy={sortBy}
            subFilters={subFilters}
            allFilters={allFilters}
            selectedFilters={selectedFilters}
            filters={filters}
            onSearchChange={setSearchValue}
            onToggleSelectedFilter={toggleSelectedFilter}
            onToggleActiveFilter={toggleActiveFilter}
            onFilterClick={filterOnClick}
            onAddSubscription={handleAddSubscription}
            onToggleSubscription={handleToggleSubscription}
            onAction={handleAction}
            onSort={onSort}
            onSubscribeAll={handleSubscribeAll}
            onUnsubscribeAll={handleUnsubscribeAll}
            onPaginationChange={handlePaginationChange}
          />
        )}
      </div>

      <AddEditSubscriptionModal
        open={isAddEditModalOpen}
        onClose={handleCloseModals}
        onSubmit={handleSubmitSubscription}
        subscription={selectedSubscription}
        clinicalTrialId={clinicalTrialId}
        notificationType={selectedKey as NotificationTypes}
        loading={subscriptionsLoading}
        existingSubscriptions={allSubscriptionsForType}
      />

      <DeleteSubscriptionModal
        open={isDeleteModalOpen}
        onClose={handleCloseModals}
        onConfirm={handleConfirmDelete}
        subscription={selectedSubscription}
        loading={subscriptionsLoading}
      />
    </>
  );
};
